import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft,
  Mic,
  Camera,
  Send,
  BookOpen,
  Play,
  HelpCircle,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Lightbulb,
  Clock,
  Image as ImageIcon,
  X,
  Volume2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LaTeXRenderer } from "@/components/LaTeXRenderer";
import { Badge } from "@/components/ui/badge";

const AskDoubt = () => {
  const navigate = useNavigate();
  const [doubt, setDoubt] = useState("");
  const [isAsked, setIsAsked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Correct answer for the quiz question: "Toluene" is index 1
  const quizCorrectIndex = 1;
  const quizOptions = ['Chlorobenzene', 'Toluene', 'Benzyl chloride', 'No reaction'];


  const suggestedQuestions = [
    "Explain SN1 vs SN2 reactions",
    "What is resonance in organic chemistry?",
    "How to calculate pH of a solution?",
    "$C_6H_6 + Cl_2 \\rightarrow C_6H_5Cl + HCl$ - Explain this reaction",
  ];

  const handleAskDoubt = () => {
    if (doubt.trim() || uploadedImage) {
      setIsLoading(true);
      setTimeout(() => {
        setIsAsked(true);
        setIsLoading(false);
      }, 1500);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startVoiceSearch = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => setIsListening(true);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setDoubt(doubt + ' ' + transcript);
      };

      recognition.start();
    } else {
      alert('Voice search is not supported in your browser');
    }
  };

  const handleReadAloud = () => {
    if (!('speechSynthesis' in window)) {
      alert('Read Aloud is not supported in your browser');
      return;
    }
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }
    const textToRead = aiResponse.replace(/\$[^$]*\$/g, '').replace(/\*\*/g, '').replace(/\\n/g, ' ').replace(/[\\#]/g, '');
    const utterance = new SpeechSynthesisUtterance(textToRead);
    utterance.rate = 0.95;
    utterance.pitch = 1;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const aiResponse = uploadedImage
    ? "Based on the chemical equation in your image: The reaction shows benzene ($C_6H_6$) reacting with chlorine ($Cl_2$) in the presence of a catalyst (typically $FeCl_3$ or $AlCl_3$) to form chlorobenzene ($C_6H_5Cl$) and hydrogen chloride ($HCl$).\\n\\nThis is an **electrophilic aromatic substitution** reaction where a hydrogen atom on the benzene ring is replaced by a chlorine atom."
    : "Chemical bonding is the attractive force that holds atoms together in compounds. There are three main types:\\n\\n1. **Ionic bonding** - electron transfer between metals and non-metals\\n2. **Covalent bonding** - electron sharing between non-metals\\n3. **Metallic bonding** - delocalized electrons in metals\\n\\nUnderstanding these concepts is crucial for predicting molecular properties and reactivity.";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="rounded-xl hover:bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-lg font-bold text-slate-900">AI Doubt Solver</h1>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {!isAsked ? (
          <div className="space-y-6">
            {/* Input Card */}
            <div className="bg-white rounded-2xl card-shadow border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  <span>Ask any chemistry question or upload an image of a problem</span>
                </div>
              </div>

              <div className="p-4">
                <Textarea
                  placeholder="Type your doubt here... e.g., Explain the mechanism of SN1 reaction in organic chemistry

You can also use LaTeX: $C_6H_6 + Cl_2 \\rightarrow C_6H_5Cl + HCl$"
                  value={doubt}
                  onChange={(e) => setDoubt(e.target.value)}
                  className="min-h-32 resize-none border-0 p-0 text-base bg-transparent focus-visible:ring-0 placeholder:text-slate-400"
                />

                {/* Image Preview */}
                {uploadedImage && (
                  <div className="relative mt-4 inline-block">
                    <img
                      src={uploadedImage}
                      alt="Uploaded"
                      className="max-h-48 rounded-lg border border-slate-200"
                    />
                    <button
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mt-4">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl hover:bg-slate-100 text-slate-500"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <ImageIcon className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`rounded-xl hover:bg-slate-100 ${isListening ? 'text-red-500 animate-pulse' : 'text-slate-500'}`}
                      onClick={startVoiceSearch}
                    >
                      <Mic className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-xl hover:bg-slate-100 text-slate-500"
                    >
                      <Camera className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="text-xs text-slate-400">
                    {doubt.length} characters
                  </div>
                </div>
              </div>
            </div>

            {/* Suggested Questions */}
            {!doubt && !uploadedImage && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-slate-600 px-1">Suggested questions</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => setDoubt(question)}
                      className="px-4 py-2 bg-white rounded-full text-sm text-slate-600 border border-slate-200 hover:border-blue-400 hover:text-blue-600 transition-colors card-shadow"
                    >
                      <LaTeXRenderer content={question.length > 40 ? question.substring(0, 40) + '...' : question} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Ask Button */}
            <Button
              onClick={handleAskDoubt}
              disabled={(!doubt.trim() && !uploadedImage) || isLoading}
              className="w-full h-14 text-base font-semibold bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 rounded-xl shadow-lg disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="h-5 w-5 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  {uploadedImage ? 'Analyze Image' : 'Ask AI'}
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Question Display */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
              <p className="text-xs font-medium text-blue-600 mb-2 uppercase tracking-wide">Your Question</p>
              {uploadedImage && (
                <img src={uploadedImage} alt="Question" className="max-h-32 rounded-lg mb-3" />
              )}
              <LaTeXRenderer content={doubt || "Image-based question"} className="text-slate-800 font-medium" />
            </div>

            {/* AI Explanation */}
            <div className="bg-white rounded-2xl card-shadow border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100 bg-gradient-to-r from-purple-50 to-blue-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500">
                    <BookOpen className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">AI Explanation</h3>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Clock className="h-3 w-3" />
                      <span>Generated in 2.3s</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5">
                <div className="prose prose-slate max-w-none">
                  <LaTeXRenderer content={aiResponse} className="text-slate-700 leading-relaxed" />
                </div>

                {/* Step by Step */}
                <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                  <p className="font-semibold text-slate-900 mb-2">Step-by-Step Mechanism:</p>
                  <ol className="space-y-2 text-sm text-slate-700">
                    <li className="flex gap-2">
                      <span className="font-bold text-blue-500">1.</span>
                      <LaTeXRenderer content="The $Cl_2$ molecule is polarized by the catalyst to form $Cl^+$ (electrophile)" />
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-blue-500">2.</span>
                      <LaTeXRenderer content="The $\\pi$-electrons of benzene attack the $Cl^+$ forming a sigma complex" />
                    </li>
                    <li className="flex gap-2">
                      <span className="font-bold text-blue-500">3.</span>
                      <LaTeXRenderer content="Loss of $H^+$ restores aromaticity, giving chlorobenzene" />
                    </li>
                  </ol>
                </div>

                {/* Read Aloud */}
                <Button
                  variant="outline"
                  size="sm"
                  className={`mt-4 ${isSpeaking ? 'border-blue-400 text-blue-600 bg-blue-50' : ''}`}
                  onClick={handleReadAloud}
                >
                  <Volume2 className={`h-4 w-4 mr-2 ${isSpeaking ? 'animate-pulse text-blue-600' : ''}`} />
                  {isSpeaking ? 'Stop Reading' : 'Read Aloud'}
                </Button>

                {/* Feedback */}
                <div className="flex items-center gap-3 mt-6 pt-4 border-t border-slate-100">
                  <span className="text-sm text-slate-500">Was this helpful?</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" className="rounded-lg hover:bg-green-50 hover:text-green-600">
                      <ThumbsUp className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="rounded-lg hover:bg-red-50 hover:text-red-600">
                      <ThumbsDown className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Recommended Video */}
            <div className="bg-white rounded-2xl card-shadow border border-slate-100 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-red-500">
                    <Play className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900">Recommended Video</h3>
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group">
                  <div className="w-20 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-lg flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <Play className="h-6 w-6 text-white fill-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-900 truncate">Electrophilic Substitution Reactions</h4>
                    <p className="text-sm text-slate-500">Organic Chemistry • 15 min</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Test Understanding */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl card-shadow border border-amber-100 overflow-hidden">
              <div className="p-4 border-b border-amber-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
                    <HelpCircle className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-bold text-slate-900">Test Your Understanding</h3>
                </div>
              </div>

              <div className="p-5 space-y-4">
                <LaTeXRenderer content="What is the major product when $C_6H_6$ reacts with $CH_3Cl$ in presence of $AlCl_3$?" className="text-slate-800 font-medium" />
                <div className="space-y-2">
                  {quizOptions.map((option, index) => {
                    const isAnswered = quizAnswer !== null;
                    const isSelected = quizAnswer === index;
                    const isCorrect = index === quizCorrectIndex;
                    let optionStyle = 'w-full p-3 text-left rounded-xl border transition-all text-slate-700 text-sm font-medium ';
                    if (!isAnswered) {
                      optionStyle += 'bg-white/70 hover:bg-white border-amber-200/50 hover:border-amber-300 cursor-pointer';
                    } else if (isCorrect) {
                      optionStyle += 'bg-green-50 border-green-400 text-green-800';
                    } else if (isSelected && !isCorrect) {
                      optionStyle += 'bg-red-50 border-red-400 text-red-800';
                    } else {
                      optionStyle += 'bg-white/50 border-amber-100 opacity-60';
                    }
                    return (
                      <button
                        key={index}
                        className={optionStyle}
                        onClick={() => { if (!isAnswered) setQuizAnswer(index); }}
                        disabled={isAnswered}
                      >
                        <span className={`inline-block w-6 h-6 rounded-full text-xs font-bold text-center leading-6 mr-3 ${isAnswered && isCorrect ? 'bg-green-200 text-green-800' :
                            isAnswered && isSelected && !isCorrect ? 'bg-red-200 text-red-800' :
                              'bg-amber-100 text-amber-700'
                          }`}>
                          {isAnswered && isCorrect ? '✓' : isAnswered && isSelected && !isCorrect ? '✗' : String.fromCharCode(65 + index)}
                        </span>
                        <LaTeXRenderer content={option} />
                      </button>
                    );
                  })}
                </div>
                {quizAnswer !== null && (
                  <div className={`p-3 rounded-xl text-sm font-medium ${quizAnswer === quizCorrectIndex ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                    {quizAnswer === quizCorrectIndex
                      ? '✓ Correct! Toluene (methylbenzene) is the product of Friedel-Crafts alkylation.'
                      : `✗ Incorrect. The correct answer is Toluene — a Friedel-Crafts alkylation reaction.`}
                  </div>
                )}
                {quizAnswer !== null && (
                  <Button variant="outline" size="sm" onClick={() => setQuizAnswer(null)} className="w-full">
                    Try Again
                  </Button>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={() => { setIsAsked(false); setDoubt(""); setUploadedImage(null); setQuizAnswer(null); }}
              className="w-full h-12 rounded-xl border-slate-200 hover:bg-slate-50"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Ask Another Question
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AskDoubt;
